/**
 * @class
 */
import {AttributeType} from '@netx/core-attribute/constants';
import {assetAttributes, getAttribute} from '@netx/core-attribute/singletons';
import {dateSubTypes1_default} from '../properties/search';
import Base from './searchRule';

export default class SearchRule_Asset extends Base {
	/** @type {module/@netx/core-asset/models/attribute} */
	get attributeTemplate() {
		if (!this.searchTargetIsMetadata()) return undefined;

		return this.assetAttributes.findWhere({
			name: this.get('elementValue2'),
		});
	}

	/** @type {boolean} */
	get isRange() {
		const elementType = this.get('elementType');
		return (
			(elementType === 3 && this.get('elementSubType1') === 6) ||
			(elementType === 4 && this.get('elementSubType2') === 6)
		);
	}

	/** @type {boolean} */
	get isRangeCompatible() {
		const attrName = this.get('elementValue2');
		const attr = this.assetAttributes.findWhere({name: attrName});
		if (!attr) return false;

		const type = attr.get('type');
		return [AttributeType.DATE, AttributeType.NUMBER].includes(type);
	}

	/**
	 * What search type are we dealing with
	 * @type {string}
	 * @default
	 */
	get searchApi() {
		return 'assets';
	}

	/** @type {object} */
	get defaults() {
		return {
			...super.defaults,
			elementType: 1, // KEYWORD
			elementSubType1: 4, // AND matching of keywords
			elementSubType2: 0,
			elementValue1: '',
			elementValue2: '',
			elementValue3: '',

			// Client attrs
			elementValue1Label: '',
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['elementValue1Label'];
	}

	/** @override */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		this.assetAttributes = assetAttributes;

		// Hey, listen
		this.on('change:elementValue1', (self, elementValue1) => {
			if (elementValue1 !== '') return;
			this.set('elementValue1Label', '');
		});
	}

	/** @override */
	getSearchObject(ignoreQuirks) {
		const searchObject = this.self();
		if (ignoreQuirks) return searchObject;

		const elementType = searchObject.elementType;

		// Attribute
		if (elementType === 3) {
			// assetId -> objectId ¯\_(ツ)_/¯
			if (searchObject.elementValue2 === 'assetId') {
				searchObject.elementValue2 = 'objectId';
			}
		}
		// DAM-16349 - in-between date search rules needs adjusting (and refactoring)
		// so it does not need the below date hacking
		// (even grosser) Do not apply this hack if it was a system defined date
		else if (
			elementType === 4 &&
			searchObject.elementSubType2 === 6 &&
			// Not the system defined ones
			[0, 1, 3].includes(searchObject.elementSubType1) &&
			searchObject.elementValue3 !== ''
		) {
			searchObject.elementValue2 = searchObject.elementValue3;
			searchObject.elementValue3 = '';
		}
		// All other dates
		else if (elementType === 4) {
			// DEVNOTE: obscure corner case bug here.
			// .. then it's an attribute Date search.
			// In the UI, Date attributes automagically show up on the 'date' search type UI, and have subtype ids other than the standard set of them.
			// But these searches are encoded differently.
			// In order for this search to work, elementSubType1 can not reference the option in the dropdown but needs to use the value of 2 to signify that we want to use a
			// date attribute - and the value we have chosen via elementSubType1 becomes the value for elementValue3
			// Therefore: change elementSubType1 to 2 and elementValue3 to the name of the attr, which is the elementSubType1 value
			const elementSubType1 = searchObject.elementSubType1;
			if (
				!dateSubTypes1_default.find((item) => item.val === elementSubType1) &&
				searchObject.elementSubType1 !== 2
			) {
				searchObject.elementValue2 = searchObject.elementValue3;
				searchObject.elementValue3 = elementSubType1;
				searchObject.elementSubType1 = 2;
			}
		}
		// Checkouts - if all is selected, java seems to want user id to be 0
		if (elementType === 13 && searchObject.elementSubType1 === 0) {
			searchObject.elementValue1 = 0;
		}

		return searchObject;
	}

	/**
	 * Is this rule searching on dynamic metadata?
	 * (as opposed to system metadata or any other type of search.)
	 * @method
	 * @return {boolean}
	 */
	searchTargetIsMetadata() {
		return (
			this.get('elementType') == 3 &&
			!_.isUndefined(getAttribute(null, this.get('elementValue2')))
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'SearchRule_Asset';
	}
}
